/**
 * Wally pre-order / special order — keep the note, the line-item property and
 * the accelerated-checkout block in sync on variant change.
 *
 * The buy-buttons block renders all three server-side per selected variant, but
 * Horizon's product-form-component only morphs the add-to-cart button (plus
 * quantity/volume bits) on variant:update, so anything else inside the form
 * goes stale when the shopper switches size. This listens for the same
 * variant:update event (bubbles to document, carries the full re-rendered
 * section HTML) and syncs:
 *   - input[name="properties[Pre-order]"] / input[name="properties[Special order]"]
 *   - .wally-preorder-note                 (visible note under the buttons; the
 *                                           special-order callout shares the class)
 *   - .wally-accelerated-checkout          (Shop Pay / Buy it now wrapper: hidden
 *                                           for pre-order and special-order sizes
 *                                           because those buttons drop line-item
 *                                           properties)
 *
 * It also holds an Add tapped while a size change is still loading (D348,
 * 29/09/2026). Horizon queues that tap and later sends it as a bare
 * { id, quantity } batch add, which drops the line-item property: #6164 went
 * through as an ordinary order on a pre-order size that way. The held tap is
 * replayed through the normal form submit once the size change and the sync
 * above are done, so the property travels with it.
 *
 * Events are scoped to the container Horizon's product-form-component listens
 * on (the section or the quick-add dialog; product tiles are skipped), so the
 * quick-add modal on collection and search pages syncs its own form, not a tile's.
 *
 * Loaded on every page by snippets/wally-tracking.liquid (quick-add modal).
 * Wording lives in snippets/wally-preorder-note.liquid and
 * snippets/wally-special-order-note.liquid; connector side in
 * shopify/re-connector/src/preorder.js and src/special-order.js.
 */

(function () {
  'use strict';

  var PROP_SELECTOR = 'input[name="properties[Pre-order]"], input[name="properties[Special order]"]';
  var NOTE_SELECTOR = '.wally-preorder-note';
  var ACCEL_SELECTOR = '.wally-accelerated-checkout';
  var FORM_SELECTOR = 'form[data-type="add-to-cart-form"]';
  var CONTAINER_SELECTOR = '.shopify-section, dialog';
  // Release a held tap even if the size change never reports back
  var FAILSAFE_MS = 8000;

  // container -> { timer, form, submitter } while a size change is in flight
  var pending = new Map();

  // Product tiles are left alone: their one-tap Add form renders its own
  // property (snippets/wally-line-note-input.liquid) and has no size change.
  function inTile(el) {
    return el instanceof Element && !!el.closest('product-card');
  }

  function containerOf(el) {
    return el instanceof Element && !inTile(el) ? el.closest(CONTAINER_SELECTOR) : null;
  }

  document.addEventListener('variant:selected', function (event) {
    var container = containerOf(event.target);
    if (!container) return;
    var entry = pending.get(container) || { timer: 0, form: null, submitter: null };
    clearTimeout(entry.timer);
    entry.timer = setTimeout(function () { release(container); }, FAILSAFE_MS);
    pending.set(container, entry);
  });

  // Window capture phase: Horizon delegates on:submit from a capture listener on
  // document (assets/component.js), so this has to run before that to stop the tap
  // reaching handleSubmit and its queue.
  window.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;
    var entry = pending.get(containerOf(form));
    if (!entry) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    entry.form = form;
    entry.submitter = event.submitter || null;
  }, true);

  document.addEventListener('variant:update', function (event) {
    if (inTile(event.target)) return;
    var container = containerOf(event.target);
    var detail = event.detail;
    if (detail && detail.data && detail.data.html) {
      var component = container
        ? container.querySelector('product-form-component')
        : detail.data.productId
          ? document.querySelector('product-form-component[data-product-id="' + detail.data.productId + '"]')
          : document.querySelector('product-form-component');
      if (component) syncForm(component, detail.data.html);
    }
    if (container) release(container);
  });

  // Replay a held tap. Deferred so Horizon's own variant:update handler (which
  // clears its in-progress flag, the variant id and the button state) has run.
  function release(container) {
    var entry = pending.get(container);
    if (!entry) return;
    clearTimeout(entry.timer);
    pending.delete(container);
    if (!entry.form) return;
    setTimeout(function () {
      if (!entry.form.isConnected) return;
      var submitter = entry.submitter && entry.submitter.form === entry.form ? entry.submitter : null;
      entry.form.requestSubmit(submitter);
    }, 0);
  }

  function syncForm(component, html) {
    var form = component.querySelector(FORM_SELECTOR);
    if (!form) return;

    // Hidden line-item property: at most one of the two, always the new one
    var newProp = html.querySelector(PROP_SELECTOR);
    form.querySelectorAll(PROP_SELECTOR).forEach(function (el) {
      if (!newProp || el.name !== newProp.name) el.remove();
    });
    var curProp = form.querySelector(PROP_SELECTOR);
    if (newProp && !curProp) {
      var anchor = form.querySelector('input[ref="variantId"]');
      if (anchor) anchor.insertAdjacentElement('afterend', newProp.cloneNode(true));
    } else if (newProp && curProp) {
      curProp.value = newProp.value;
    }

    // Visible note under the buy buttons: replace wholesale (kind may change)
    var curNote = form.querySelector(NOTE_SELECTOR);
    var newNote = html.querySelector(NOTE_SELECTOR);
    if (curNote && !newNote) {
      curNote.remove();
    } else if (!curNote && newNote) {
      var buttons = form.querySelector('.product-form-buttons');
      if (buttons) buttons.insertAdjacentElement('afterend', newNote.cloneNode(true));
    } else if (curNote && newNote) {
      curNote.replaceWith(newNote.cloneNode(true));
    }

    // Accelerated checkout: server omits the wrapper for marked sizes. Hide the
    // live one rather than removing it (Shop Pay hydrates once), and only
    // insert a clone when the page loaded on a marked size and had none.
    var curAccel = form.querySelector(ACCEL_SELECTOR);
    var newAccel = html.querySelector(ACCEL_SELECTOR);
    if (curAccel) {
      curAccel.hidden = !newAccel;
    } else if (newAccel) {
      var addToCart = form.querySelector('.product-form-buttons');
      if (addToCart) addToCart.insertAdjacentElement('beforeend', newAccel.cloneNode(true));
    }
  }
})();
